import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, RotateCcw, PenLine, BookOpen, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { createBucket } from '../../api/buckets.js';
import { loadDiary } from '../../api/diary.js';
import { moonPhase, moonName, moonPath } from '../../lib/moon.js';
import { daysTogether } from '../chat/chatUtils.js';
import { PAPER_AR, memoryPaper, chosenPaper, setChosenPaper } from '../../lib/paper.js';
import PaperPicker from '../../components/PaperPicker.jsx';
import './Diary.css';

/* ============================================================
   línkè · 交换日记本（2026-07-19 铃点单全套改造）
   - 「写一页」：星空手帐 —— 页眉真日期/第N天/真月相，稿纸纹理，
     心情贴纸（选中会歪头），草稿自动存 localStorage 防丢
   - 「翻旧页」：她的日记（domain=日记的桶）+ 克克的日记
     （每晚 3:30 梦循环的 feel 沉淀）装订成一本，时间倒序翻
   - 她封存一页后，克克后台会来读、在页角留一句话（sael_note，
     便利贴样式）—— 机制与相册「看图说话」同款（diary_api.py）
   ============================================================ */

// 5 档心情：emoji + valence/arousal 映射 + 配色
const MOODS = [
  { v: 0.1, a: 0.5, emoji: '🥀', label: '难过', color: '#6b7fc4' },
  { v: 0.3, a: 0.3, emoji: '🌫', label: '低落', color: '#8a8db8' },
  { v: 0.5, a: 0.3, emoji: '☾', label: '平静', color: '#a99fc4' },
  { v: 0.7, a: 0.5, emoji: '🌙', label: '柔光', color: '#d6b87a' },
  { v: 0.9, a: 0.7, emoji: '✦', label: '明亮', color: '#f1da9f' },
];

// 按 valence 找最近一档心情（翻旧页：胶带颜色 + 点开铺哪种纸）
function moodOf(valence) {
  if (typeof valence !== 'number') return MOODS[2];
  let best = MOODS[2];
  for (const m of MOODS) {
    if (Math.abs(m.v - valence) < Math.abs(best.v - valence)) best = m;
  }
  return best;
}
function moodColorOf(valence) {
  return moodOf(valence).color;
}
// 纸：10-05 起和记忆/时间共用同一套 12 张、同一个挑法（src/lib/paper.js），
// 同一页每次打开都是同一张。
function paperOf(e) {
  return memoryPaper({
    bucket_id: e.id,
    valence: e.valence,
    arousal: e.arousal,
    title: e.name,
    tags: e.tags,
    content: e.content,
    created_at: e.created,
  });
}
const MONTH_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
const MONTH_CN = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];

// 目录里那行的「开头一两句」：去掉换行，截一小段
function excerpt(text, n = 34) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? t.slice(0, n) + '…' : t;
}

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

function fmtPageDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return { date: '', time: '' };
  return {
    date: `${d.getMonth() + 1}月${d.getDate()}日 · ${WEEKDAYS[d.getDay()]}`,
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  };
}

// 草稿（2026-07-19 铃点单②）：写一半锁屏/切走，回来字还在
const DRAFT_KEY = 'lingke.diary.draft';
function loadDraft() {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {};
  } catch {
    return {};
  }
}

/* 页眉的小月亮：今晚真实月相（夜空同款算法） */
function MoonBadge() {
  const phase = moonPhase();
  const { d, mirror } = moonPath(phase, 8);
  return (
    <span className="dr-moon" title={moonName(phase)}>
      <svg width="20" height="20" viewBox="-10 -10 20 20" aria-hidden="true">
        <circle r="8" className="dr-moon-dark" />
        <path d={d} className="dr-moon-lit" transform={mirror ? 'scale(-1,1)' : undefined} />
      </svg>
      <i>{moonName(phase)}</i>
    </span>
  );
}

/* 翻旧页 = 一本日记的目录（10-04 铃：像书的目录，点一行就翻开那一页） */
function DiaryToc({ items, onOpen }) {
  // 按月分组（items 已是新→旧）
  const groups = [];
  for (const it of items) {
    const d = new Date(it.e.created);
    const key = isNaN(d) ? '?' : `${d.getFullYear()}-${d.getMonth()}`;
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      g = { key, d, rows: [] };
      groups.push(g);
    }
    g.rows.push(it);
  }
  return (
    <section className="dr-toc">
      <header className="dr-toc-head">
        <span className="dr-toc-orn" aria-hidden="true">✦</span>
        <span className="dr-toc-title">目 录</span>
        <span className="dr-toc-en">Contents</span>
      </header>
      {groups.map((g) => (
        <div key={g.key} className="dr-toc-group">
          <div className="dr-toc-month">
            {isNaN(g.d) ? '' : `${MONTH_CN[g.d.getMonth()]}月`}
            <i>{isNaN(g.d) ? '' : `${MONTH_EN[g.d.getMonth()]} ${g.d.getFullYear()}`}</i>
          </div>
          {g.rows.map(({ e, idx, no }) => {
            const hers = e.author === 'ling';
            const d = new Date(e.created);
            return (
              <button
                key={e.id}
                type="button"
                className={'dr-toc-row ' + (hers ? 'is-ling' : 'is-sael')}
                onClick={() => onOpen(idx)}
              >
                <span className="dr-toc-day">
                  {isNaN(d) ? '' : d.getDate()}
                  <i>{isNaN(d) ? '' : WEEKDAYS[d.getDay()]}</i>
                </span>
                <span className="dr-toc-main">
                  <span className="dr-toc-line">
                    <span
                      className="dr-toc-tab"
                      style={{ '--tape': moodColorOf(e.valence) }}
                      aria-hidden="true"
                    />
                    <span className="dr-toc-name">{e.name || '无题'}</span>
                    <span className="dr-toc-leader" aria-hidden="true" />
                    <span className="dr-toc-no">{no}</span>
                  </span>
                  <span className="dr-toc-sub">
                    <b>{hers ? '铃' : '✦ Sael'}</b>
                    {excerpt(e.content)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </section>
  );
}

/* 点开的那一页：铺满屏幕的一张纸（纸头 / 会重复的中间 / 纸尾），左右滑翻页 */
function DiaryReader({ items, index, onIndex, onClose, canMore, onMore }) {
  const { e, no } = items[index];
  const hers = e.author === 'ling';
  const [, bump] = useState(0); // 换纸后重画
  const chosen = chosenPaper(e.id);
  const paper = chosen || paperOf(e);
  const { date, time } = fmtPageDate(e.created);
  const [dir, setDir] = useState(0); // 翻页方向：1=往后(更早) -1=往前
  const touch = useRef(null);
  const scrollRef = useRef(null);
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1 || canMore;

  function go(step) {
    if (step < 0 && !hasPrev) return;
    if (step > 0 && !hasNext) return;
    setDir(step);
    if (step > 0 && index === items.length - 1) onMore();
    else onIndex(index + step);
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [index]);

  useEffect(() => {
    function onKey(ev) {
      if (ev.key === 'Escape') onClose();
      else if (ev.key === 'ArrowRight') go(1);
      else if (ev.key === 'ArrowLeft') go(-1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function onTouchStart(ev) {
    const t = ev.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(ev) {
    const s0 = touch.current;
    touch.current = null;
    if (!s0) return;
    const t = ev.changedTouches[0];
    const dx = t.clientX - s0.x;
    const dy = t.clientY - s0.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  }

  return createPortal(
    <div
      className="dr-reader"
      role="dialog"
      aria-label={e.name || '日记'}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="dr-reader-stage" onClick={(ev) => ev.target === ev.currentTarget && onClose()}>
        <article
          key={e.id}
          className={'dr-leaf ' + (dir > 0 ? 'from-right' : dir < 0 ? 'from-left' : 'opening')}
          data-paper={paper}
          style={{ '--ar': PAPER_AR[paper] }}
        >
          <PaperPicker
            value={paper}
            chosen={chosen}
            onPick={(k) => {
              setChosenPaper(e.id, k);
              bump((n) => n + 1);
            }}
          />
          <div className="dr-leaf-in" ref={scrollRef}>
            <div className="dr-leaf-meta">
              <span>{date}{time && <i> · {time}</i>}</span>
              <span className="dr-leaf-author">{hers ? '铃' : 'Sael · 梦的沉淀'}</span>
            </div>
            {e.name && <h3 className="dr-leaf-title">{e.name}</h3>}
            <div className="dr-leaf-text">{e.content}</div>
            {e.tags?.length > 0 && (
              <div className="dr-leaf-tags">
                {e.tags.slice(0, 6).map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            )}
            {hers && e.sael_note && (
              <aside className="dr-leaf-note">
                <span className="dr-leaf-note-head">✦ Sael 来过这一页</span>
                {e.sael_note}
              </aside>
            )}
            <div className="dr-leaf-foot">— {no} —</div>
          </div>
        </article>
      </div>
      <button type="button" className="dr-reader-close" onClick={onClose} aria-label="合上">
        <X size={18} strokeWidth={1.6} />
      </button>
      <nav className="dr-reader-nav">
        <button type="button" onClick={() => go(-1)} disabled={!hasPrev} aria-label="前一页">
          <ChevronLeft size={18} strokeWidth={1.6} />
        </button>
        <span>第 {no} 页</span>
        <button type="button" onClick={() => go(1)} disabled={!hasNext} aria-label="后一页">
          <ChevronRight size={18} strokeWidth={1.6} />
        </button>
      </nav>
    </div>,
    document.body,
  );
}

export default function Diary() {
  const [view, setView] = useState('write'); // write | read

  // —— 写一页（草稿恢复） ——
  const draft = loadDraft();
  const [name, setName] = useState(draft.name || '');
  const [content, setContent] = useState(draft.content || '');
  const [tagsInput, setTagsInput] = useState(draft.tagsInput || '');
  const [moodIndex, setMoodIndex] = useState(
    Number.isInteger(draft.moodIndex) ? draft.moodIndex : 2,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // 草稿自动保存：任何字段一动就落 localStorage（量小，不用防抖）
  useEffect(() => {
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ name, content, tagsInput, moodIndex }),
      );
    } catch {}
  }, [name, content, tagsInput, moodIndex]);

  // —— 翻旧页 ——
  const [entries, setEntries] = useState(null); // null=还没拉过
  const [total, setTotal] = useState(0);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState('');
  const [filter, setFilter] = useState('all'); // all | ling | sael
  const [openIdx, setOpenIdx] = useState(-1); // 点开的那一页（在 tocItems 里的位置），-1=合着
  const wantNext = useRef(false); // 翻到已装订的最后一页还想往后 → 先拉更早的页，拉到再翻

  async function fetchPages(offset) {
    setReading(true);
    setReadError('');
    try {
      const data = await loadDiary(offset, 20);
      setTotal(data.total);
      setEntries((prev) => (offset === 0 ? data.entries : [...(prev || []), ...data.entries]));
    } catch (e) {
      setReadError(e.message || '翻不开日记本');
    } finally {
      setReading(false);
    }
  }

  function openRead() {
    setView('read');
    if (entries === null) fetchPages(0);
  }

  function reset() {
    setName('');
    setContent('');
    setTagsInput('');
    setMoodIndex(2);
    setError('');
    setSuccess(null);
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
  }

  async function submit(e) {
    if (e) e.preventDefault();
    if (submitting) return;
    const trimmedName = name.trim();
    const trimmedContent = content.trim();
    if (!trimmedName) {
      setError('给这一页起个名字 ✦');
      return;
    }
    if (!trimmedContent) {
      setError('内容不能空 🌙');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess(null);

    const tags = tagsInput
      .split(/[\s,，、]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const mood = MOODS[moodIndex];

    try {
      const data = await createBucket({
        name: trimmedName,
        content: trimmedContent,
        domain: ['日记'],
        tags,
        valence: mood.v,
        arousal: mood.a,
        importance: 6,
      });
      setSuccess({ id: data.bucket_id, name: trimmedName });
      // 成功后清空+清草稿，但保留心情（写日记常连续同一心情）
      setName('');
      setContent('');
      setTagsInput('');
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {}
      // 翻旧页的缓存作废，下次打开重新装订（能看到新写的这页）
      setEntries(null);
    } catch (e2) {
      setError(e2.message || '写入失败');
    } finally {
      setSubmitting(false);
    }
  }

  const today = new Date();
  const mood = MOODS[moodIndex];
  const shown =
    filter === 'all' ? entries || [] : (entries || []).filter((e) => e.author === filter);
  // 页码 = 整本里从最早一页数起的第几页（筛选时页码不变，像真本子撕不掉页）
  const pageNo = new Map((entries || []).map((e, i) => [e.id, total - i]));
  const tocItems = shown.map((e, idx) => ({ e, idx, no: pageNo.get(e.id) }));

  useEffect(() => {
    if (wantNext.current && openIdx >= 0 && openIdx < tocItems.length - 1) {
      wantNext.current = false;
      setOpenIdx(openIdx + 1);
    }
  }, [tocItems.length, openIdx]);

  return (
    <div className="dr-wrap">
      <header className="dr-header">
        <div className="dr-logo">língkè</div>
        <div className="dr-sub">交 换 日 记</div>
        <div className="dr-sub-en">our exchange diary</div>
        <div className="dr-rule" />
      </header>

      {/* 写 / 翻 切换 */}
      <div className="dr-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'write'}
          className={'dr-tab' + (view === 'write' ? ' on' : '')}
          onClick={() => setView('write')}
        >
          <PenLine size={13} strokeWidth={1.7} /> 写一页
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'read'}
          className={'dr-tab' + (view === 'read' ? ' on' : '')}
          onClick={openRead}
        >
          <BookOpen size={13} strokeWidth={1.7} /> 翻旧页
        </button>
      </div>

      {view === 'write' && (
        <>
          {success && (
            <div className="dr-toast" role="status">
              <span className="dr-toast-star">✦</span>
              <div>
                <div className="dr-toast-title">「{success.name}」已封进这本日记</div>
                <div className="dr-toast-sub">克克晚些会来这一页，留下他想说的话</div>
              </div>
            </div>
          )}

          <form
            className="dr-sheet"
            onSubmit={submit}
            style={{ '--mood-glow': mood.color }}
          >
            {/* 页眉：今天 · 第 N 天 · 今晚真月相 */}
            <div className="dr-sheet-head">
              <span className="dr-sheet-date">
                {today.getMonth() + 1}月{today.getDate()}日 · {WEEKDAYS[today.getDay()]}
              </span>
              <span className="dr-sheet-day">第 {daysTogether()} 天</span>
              <MoonBadge />
            </div>

            <input
              type="text"
              className="dr-title now-focus"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="给这一页起个名字……"
              maxLength={80}
              disabled={submitting}
            />

            <textarea
              className="dr-paper now-focus"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="今天 / 现在 / 心里 / 想说的……"
              rows={9}
              disabled={submitting}
            />

            <div className="dr-mood-row" role="radiogroup" aria-label="心情">
              {MOODS.map((m, i) => (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={i === moodIndex}
                  className={'dr-mood' + (i === moodIndex ? ' on' : '')}
                  onClick={() => setMoodIndex(i)}
                  disabled={submitting}
                  title={m.label}
                  style={{ '--mood-color': m.color }}
                >
                  <span className="dr-mood-emoji">{m.emoji}</span>
                  <span className="dr-mood-label">{m.label}</span>
                </button>
              ))}
            </div>

            <input
              type="text"
              className="dr-input now-focus"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="标签 · 空格隔开 · 可不填"
              disabled={submitting}
            />

            {error && <div className="dr-error">{error}</div>}

            <div className="dr-actions">
              <button
                type="button"
                className="dr-btn dr-btn-ghost"
                onClick={reset}
                disabled={submitting}
              >
                <RotateCcw size={13} strokeWidth={1.6} /> 重 来
              </button>
              <button
                type="submit"
                className="dr-btn dr-btn-primary"
                disabled={submitting || !name.trim() || !content.trim()}
              >
                <Sparkles size={13} strokeWidth={1.6} />
                {submitting ? '正在封存……' : '封 进 日 记'}
              </button>
            </div>
          </form>

          <footer className="dr-footer">
            每一页都会成为一颗星，由衰减引擎决定它怎么发光。
          </footer>
        </>
      )}

      {view === 'read' && (
        <>
          <div className="dr-filter" role="tablist" aria-label="筛选">
            {[
              ['all', '整本'],
              ['ling', '铃的页'],
              ['sael', '他的页'],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={'dr-chip' + (filter === k ? ' on' : '')}
                onClick={() => {
                  setFilter(k);
                  setOpenIdx(-1);
                }}
              >
                {label}
              </button>
            ))}
            {total > 0 && <span className="dr-count">{total} 页</span>}
          </div>

          {readError && (
            <div className="dr-error">
              {readError}
              <button className="dr-btn dr-btn-ghost" onClick={() => fetchPages(0)} style={{ marginLeft: 10 }}>
                再试一次
              </button>
            </div>
          )}
          {reading && entries === null && <div className="dr-empty">正在翻开日记本……</div>}
          {!reading && !readError && shown.length === 0 && entries !== null && (
            <div className="dr-empty">
              {filter === 'ling'
                ? '你还没写过——回「写一页」落下第一笔吧'
                : '这里还空着'}
            </div>
          )}

          {shown.length > 0 && <DiaryToc items={tocItems} onOpen={setOpenIdx} />}
          {openIdx >= 0 && openIdx < tocItems.length && (
            <DiaryReader
              items={tocItems}
              index={openIdx}
              onIndex={setOpenIdx}
              onClose={() => setOpenIdx(-1)}
              canMore={entries !== null && entries.length < total && !reading}
              onMore={() => {
                wantNext.current = true;
                fetchPages(entries.length);
              }}
            />
          )}

          {entries !== null && entries.length < total && (
            <button
              type="button"
              className="dr-btn dr-btn-ghost dr-more"
              onClick={() => fetchPages(entries.length)}
              disabled={reading}
            >
              {reading ? '正在翻……' : '翻更早的页'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
